import * as Sentry from '@sentry/nextjs';
import { User } from 'firebase/auth';
import { onValue, ref } from 'firebase/database';
import { HASURA_CLAIMS_KEY } from 'constants/auth';
import { HasuraClaims } from 'constants/auth';
import { auth, database } from 'services/client/firebase';

export type FirebaseUserOrNull = User | null;

const VALUEOUT_TIMEOUT_MS = 40_000;

type TokenOrNull = string | null;
interface UpdateResponse {
  user: FirebaseUserOrNull;
  token: TokenOrNull;
  userId: string | null | undefined;
  claims: HasuraClaims | null;
}
type FirebaseCallback = ({ user, token }: UpdateResponse) => void;

// const MINUTE = 60 * 1000;
// const HOUR = 60 * MINUTE;
// const DAY = 24 * HOUR;
// const FIREBASE_REFRESH_POLL_TIME = 20 * MINUTE;
// const MAX_LOGIN_TIME = 7 * DAY;

let token: TokenOrNull = null;

export const updateViewerToken = (
  user: FirebaseUserOrNull,
  shouldForceRefresh?: boolean,
): Promise<UpdateResponse> => {
  return new Promise(async (resolve) => {
    if (user) {
      const firebaseToken = await user.getIdToken(shouldForceRefresh);
      const idTokenResult = await user.getIdTokenResult();
      const userId = idTokenResult.claims.userId as string;
      const hasuraClaim = idTokenResult.claims[HASURA_CLAIMS_KEY] as HasuraClaims;
      token = firebaseToken;

      if (hasuraClaim) {
        return resolve({ user, token, userId, claims: hasuraClaim });
      } else {
        // NOTE: This is using the older Firebase database, but since it's transient data, it should be trivial to change or even simply use a Poll to look for claims
        // Check if refresh is required.
        const metadataRef = ref(database, 'metadata/' + user.uid + '/refreshTime');
        let settled = false;

        const unsubscribe = onValue(metadataRef, async (data) => {
          if (!data.exists() || settled) return;
          settled = true;
          clearTimeout(timeoutId);
          unsubscribe();
          // Force refresh to pick up the latest custom claims changes.
          token = await user.getIdToken(true);
          const idTokenResult = await user.getIdTokenResult();
          const userId = idTokenResult.claims.userId as string;
          const hasuraClaim = idTokenResult.claims['https://hasura.io/jwt/claims'] as HasuraClaims;

          return resolve({ user, token, userId, claims: hasuraClaim });
        });

        const timeoutId = setTimeout(async () => {
          if (settled) return;
          settled = true;
          unsubscribe();

          Sentry.captureException(
            new Error('updateViewerToken timed out waiting for RTDB metadata'),
            {
              extra: { uid: user.uid },
            },
          );

          // Last-resort: force refresh in case claims were set but RTDB write was missed
          try {
            token = await user.getIdToken(true);
            const idTokenResult = await user.getIdTokenResult();
            const userId = idTokenResult.claims.userId as string;
            const hasuraClaim = idTokenResult.claims[HASURA_CLAIMS_KEY] as HasuraClaims;

            if (hasuraClaim) {
              return resolve({ user, token, userId, claims: hasuraClaim });
            }
          } catch (e) {
            // Ignore — we'll resolve with null claims below
          }

          return resolve({ user, token, userId: null, claims: null });
        }, VALUEOUT_TIMEOUT_MS);
      }
    } else {
      token = null;
      return resolve({ user, token, userId: null, claims: null });
    }
  });
};

// NOTE: This should probably be a React hook and manage sub/unsub of these methods.
// However, with the client-side routing, it seems to only mount once and work correctly,
// but should be monitored for potential leaks.
export const watchViewerTokenChanges = (callback: FirebaseCallback) => {
  auth.onAuthStateChanged(async (user) => {
    updateViewerToken(user, true).then((res: UpdateResponse) => callback(res));
  });
  // auth.onIdTokenChanged(async (user) => {
  //   updateViewerToken(user).then((res: UpdateResponse) => callback(res));
  // });
};

export const getViewerToken = async () => {
  const currentUserToken = await auth?.currentUser?.getIdToken();
  return currentUserToken;
};

export const getAuthHeaders = async () => {
  const token = await getViewerToken();

  if (!token) {
    return {};
  }

  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};
